import {
  LayoutDashboard,
  Target,
  Timer,
  Building2,
  Users,
  Ship,
  ClipboardList,
  ListChecks,
  Eye,
  Sparkles,
  Settings2,
  Bell,
  ShieldAlert,
  Stethoscope,
  SlidersHorizontal,
  UserCircle2,
  Flame,
  TrendingUp,
  BarChart3,
  Gauge,
  FileText,
  HelpCircle,
  Wrench,
  Anchor,
  Contact,
  BookOpen,
  type LucideIcon,
} from "lucide-react";

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** If true, item is highlighted in the AI accent treatment */
  ai?: boolean;
  /** Match nested routes too */
  matchPrefix?: boolean;
  /** RBAC feature key — when set, the item is hidden unless the user can
   *  view that feature. Items without a feature are always shown. */
  feature?: string;
}

export interface NavGroup {
  label?: string;
  items: NavItem[];
}

export type Role = "super_admin" | "admin" | "vessel" | "shore_staff";

export const ROLE_BASE: Record<Role, string> = {
  "super_admin": "/super_admin",
  "admin":       "/admin",
  "vessel":      "",
  "shore_staff": "/shore_staff",
};

/**
 * The Dashboard ("home"/start destination) path for a role — the base of the
 * back stack for bottom-nav tab navigation. This is the Dashboard nav item's
 * `to`, NOT roleHomePath() (which returns the bare role base like "/admin" that
 * only redirects to the dashboard). Kept in sync with the Dashboard items above.
 */
export function homePathForRole(role: Role): string {
  return `${ROLE_BASE[role]}/dashboard`;
}

export function getNavForRole(role: Role, rank?: string | null): NavGroup[] {
  const base = ROLE_BASE[role];
  const p = (path: string) => `${base}${path}`;
  const isMaster = (rank ?? "").toLowerCase() === "master";

  // ---- Super admin ----
  if (role === "super_admin") {
    // Grouped by WHAT THE THING IS, not by verb. A single "Manage" bucket put
    // the tenant list, three question libraries, two lookup tables and a job
    // console side by side in one undifferentiated column, so finding anything
    // meant reading all of it. The four groups below are the distinctions a
    // super admin actually works in.
    return [
      {
        // The two starting points: the platform overview, and the tenants it
        // is an overview OF. Unlabelled, like the office shell's top group.
        items: [
          { label: "Dashboard",           to: p("/dashboard"),           icon: LayoutDashboard },
          { label: "Companies",           to: p("/company-management"),  icon: Building2,    matchPrefix: true },
        ],
      },
      {
        // The master content every company inherits. Editing anything here has
        // platform-wide blast radius, which is why it is one group and not
        // scattered among the lookup tables below.
        label: "Global content",
        items: [
          { label: "Checklists",          to: p("/checklist-management"),icon: ListChecks,   matchPrefix: true },
          { label: "TMSA question bank",  to: p("/tmsa-question-bank"),   icon: Target },
          { label: "DryBMS framework",    to: p("/drybms-framework"),    icon: Anchor },
          // Root cause list is Company Admin only — intentionally not
          // surfaced in the super_admin sidebar.
        ],
      },
      {
        // Lookup lists the rest of the app selects FROM. Rarely edited, and
        // never during an incident — deliberately not next to the switches.
        label: "Reference data",
        items: [
          { label: "PSC codes",           to: p("/psc-codes"),           icon: ShieldAlert },
          { label: "Ranks & Vessel Types", to: p("/ranks"),             icon: UserCircle2 },
        ],
      },
      {
        // Runtime controls rather than content: what is switched on, and what
        // the platform is currently doing. Both are incident-time destinations.
        label: "Platform",
        items: [
          { label: "AI features",         to: p("/ai-features"),          icon: Sparkles },
          { label: "Background jobs",     to: p("/background-jobs"),      icon: Timer },
        ],
      },
    ];
  }

  // ---- Office shell (company admin + shore staff share ONE shell) ----
  // shore_staff sees the same nav; every item is `feature`-gated so the sidebar
  // filters it to what the role's permissions allow (and empty groups drop out).
  // Links auto-prefix with ROLE_BASE[role], so p("/dashboard") stays the shore
  // dashboard for shore_staff.
  if (role === "admin" || role === "shore_staff") {
    return [
      {
        items: [
          { label: "Dashboard",           to: p("/dashboard"),           icon: LayoutDashboard, feature: "dashboard" },
          { label: "AI insights",         to: p("/ai"),                  icon: Sparkles, ai: true, feature: "ai_insights" },
        ],
      },
      {
        label: "Operations",
        items: [
          { label: "Inspections",         to: p("/manage-inspections"),  icon: ClipboardList, matchPrefix: true, feature: "inspections" },
          { label: "Pre-inspection (PIQ)",to: p("/pre-inspections"),     icon: ClipboardList, matchPrefix: true, feature: "inspections" },
          { label: "Observations",        to: p("/observations"),        icon: Eye, feature: "observations" },
          { label: "TMSA",                to: p("/tmsa"),                icon: Target, matchPrefix: true, feature: "tmsa" },
          { label: "DryBMS",              to: p("/drybms"),              icon: Anchor, matchPrefix: true, feature: "drybms" },
          { label: "SMS Library",         to: p("/sms"),                 icon: BookOpen, matchPrefix: true, feature: "sms_documents" },
        ],
      },
      {
        label: "Manage",
        items: [
          { label: "Vessels",             to: p("/vessel-management"),   icon: Ship, feature: "vessels" },
          { label: "Crew",                to: p("/crew"),                icon: Contact, feature: "users" },
          { label: "Users & Roles",       to: p("/user-management"),     icon: Users, matchPrefix: true, feature: "users" },
          { label: "Inspectors",          to: p("/inspector-management"),icon: UserCircle2, matchPrefix: true, feature: "users" },
          { label: "Checklists",          to: p("/checklist-management"),icon: ListChecks, matchPrefix: true, feature: "checklists" },
          { label: "Finding types",       to: p("/finding-types"),       icon: SlidersHorizontal, feature: "finding_types" },
          { label: "Root cause list",     to: p("/root-cause-categories"), icon: Stethoscope, feature: "root_causes" },
        ],
      },
      {
        label: "Analytics",
        items: [
          { label: "Analytics",           to: p("/analytics"),           icon: BarChart3, feature: "fleet_analytics" },
          { label: "Vetting Priority",    to: p("/vetting-queue"),       icon: Gauge, feature: "fleet_analytics" },
          { label: "Risk Heat Map",       to: p("/risk-heat-map"),       icon: Flame, feature: "risk_heat_map" },
          { label: "Cycle Comparison",    to: p("/cycle-comparison"),    icon: TrendingUp, feature: "cycle_comparison" },
          { label: "Reports",             to: p("/reports"),             icon: FileText, matchPrefix: true, feature: "reports" },
          // "Import Data" (SIRE 2.0 report import) relocated to a button on
          // the Inspections page (next to "Historical Data Import").
        ],
      },
      // Settings lives in SECONDARY_ITEMS for every role — don't repeat here.
    ];
  }

  // ---- Vessel ----
  if (role === "vessel") {
    return [
      {
        items: [
          { label: "Dashboard",           to: "/dashboard",              icon: LayoutDashboard },
        ],
      },
      {
        label: "Inspections",
        items: [
          { label: "Inspections",         to: "/manage-inspections",     icon: ClipboardList, matchPrefix: true },
          { label: "Pre-inspection (PIQ)",to: "/pre-inspections",        icon: ClipboardList, matchPrefix: true },
          { label: "Observations",        to: "/observations",           icon: Eye },
          // Crew see only rectifications assigned to them; the Master sees the
          // whole vessel's queue (the page differentiates by role/rank).
          { label: isMaster ? "Rectifications" : "My rectifications", to: "/rectifications", icon: Wrench, matchPrefix: true },
        ],
      },
      {
        label: "Reference",
        items: [
          // The company's procedures and manuals; readable offline once synced.
          { label: "SMS Library",         to: "/sms",                    icon: BookOpen, matchPrefix: true, feature: "sms_documents" },
        ],
      },
      // Crew logins are managed by the vessel's Master only (the backend
      // re-checks rank=Master + own-vessel scope on every call). Hidden
      // for non-Master crew, who'd only hit a 403.
      ...(isMaster
        ? [{
            label: "Manage",
            items: [
              { label: "Crew",            to: "/crew-management",        icon: Users, matchPrefix: true },
              { label: "Inspectors",      to: "/inspector-management",   icon: UserCircle2, matchPrefix: true },
            ],
          }]
        : []),
    ];
  }

  // Fallback (should be unreachable — all four roles handled above).
  return [];
}

/** Mobile bottom-nav: max 5 items so we trim to the most-used. */
export function getMobileNavForRole(role: Role): NavItem[] {
  const all = getNavForRole(role).flatMap((g) => g.items);
  const dashboard    = all.find((n) => n.label === "Dashboard");
  const inspections  = all.find((n) => n.label.includes("Inspection"));
  const ai           = all.find((n) => n.ai);
  const observations = all.find((n) => n.label === "Observations");

  const items: NavItem[] = [];
  if (dashboard) items.push(dashboard);
  if (inspections) items.push(inspections);
  if (ai) items.push(ai);
  if (observations) items.push(observations);
  // Settings is added as a separate "More" tab in the bottom nav itself
  return items;
}

export const SECONDARY_ITEMS = (role: Role): NavItem[] => {
  const base = ROLE_BASE[role];
  return [
    { label: "Notifications", to: `${base}/notifications`, icon: Bell },
    // Profile was merged into Settings — the same page now renders the
    // appropriate sections per role (everyone sees Profile; admin & super-
    // admin additionally see Branding + Company; super-admin also gets the
    // AI Usage panel).
    { label: "Settings",      to: `${base}/settings`,       icon: Settings2 },
    // Help Center is a single public route (not role-prefixed); the page
    // itself tailors what it shows to the signed-in user's role.
    { label: "Help",          to: "/help",                  icon: HelpCircle },
  ];
};
